import {
  cities,
  countries
} from '@features/admin/components/location-details/cityMapping';
import {
  isValidBloodGroup,
  isValidInsureValue
} from '@features/member-management/helper';
import { UserProfileCustomField } from '@features/member-management/types';
import { isValidPhoneNumber } from '@helpers/generic';
import { AddMemberInput, MemberListType, TermData } from '@types';
import { parseDateAuto } from '@utils/date';
import { checkArraysAreSame, roundTo2 } from '@utils/generic';
import {
  isInvalidAmount,
  isInvalidEmail,
  isInvalidMemberName
} from '@utils/validations';

import { defaultTemplateHeaders, PaymentCollectionOptions } from '../constants';
import { parseDateString } from '../helper';
import { customTermFileValidation } from './customTerm';

const hasDecimalPoint = (
  amount: string | number | undefined | null
): boolean => {
  if (!amount) return false;
  return String(amount).includes('.');
};

const hasMoreThanTwoDecimalPlaces = (
  amount: string | number | undefined | null
): boolean => {
  if (!amount) return false;
  const str = String(amount);
  if (!str.includes('.')) return false;
  const decimalPart = str.split('.')[1] || '';
  return decimalPart.length > 2;
};

export const isInvalidMemberList = (
  memberList: MemberListType[],
  maxAmountLimit: string,
  minAmountLimit: string,
  nameMaxLength: string,
  nameMinLength: string,
  countryCodeMapping: Record<string, string>
): boolean => {
  const errors: string[] = [];
  memberList.forEach((member) => {
    // check for amount
    if (member.termsList?.length && member.termsList?.length > 0) {
      if (
        member.termsList.some(
          (term) =>
            Number(term.dueAmount) !== 0 &&
            isInvalidAmount(
              term.dueAmount,
              maxAmountLimit,
              minAmountLimit,
              true
            )
        )
      ) {
        errors.push(`Invalid term amount for ${member.name}`);
      }
    } else if (
      member.amount &&
      member.amount !== '0' &&
      isInvalidAmount(member.amount, maxAmountLimit, minAmountLimit, true)
    ) {
      errors.push(`Invalid amount for ${member.name}`);
    }

    // check for name
    if (isInvalidMemberName(member.name, nameMaxLength, nameMinLength)) {
      errors.push(`Invalid name for ${member.name}`);
    }

    // check for primary phone number
    if (
      !member.primaryNumber ||
      (member.primaryNumber &&
        !isValidPhoneNumber(
          member.primaryNumber,
          countryCodeMapping[member.primaryNumberCountryCode || '']
        ))
    ) {
      errors.push(`Invalid phone number for ${member.name}`);
    }

    // check for alternate phone number
    if (
      member.altNumber &&
      member.altNumber !== 'null' &&
      !isValidPhoneNumber(
        member.altNumber,
        countryCodeMapping[member.altNumberCountryCode || '']
      )
    ) {
      errors.push(`Invalid alternate phone number for ${member.name}`);
    }
  });

  return errors.length > 0;
};

export const invalidCSVErrorMessage =
  'Invalid data found. Update the uploaded file & try again.';

const validateHeaders = (
  dataHeaders: string[],
  feeCategories: string[],
  isEmailEnabled: boolean,
  isAmountPaidTillDateEnabled: boolean,
  isOnce: boolean,
  customFields: UserProfileCustomField[],
  termsName: string[],
  isLeadSquared: boolean,
  isCustomTerm: boolean,
  isEndDateEnabled?: boolean,
  isAttendanceBasedFee?: boolean,
  isSessionBased?: boolean
): string[] => {
  const buildTemplateHeaders = (includeUserProfile: boolean) =>
    defaultTemplateHeaders(
      feeCategories,
      isEmailEnabled,
      isAmountPaidTillDateEnabled,
      isOnce,
      customFields,
      termsName,
      includeUserProfile,
      isLeadSquared,
      isCustomTerm,
      isEndDateEnabled,
      isAttendanceBasedFee,
      isSessionBased
    );

  const stripEmpty = (headers: string[]) =>
    headers.filter((header) => header !== '' && !header.startsWith('_'));

  const headersMatch =
    checkArraysAreSame(
      stripEmpty(dataHeaders),
      stripEmpty(buildTemplateHeaders(false))
    ) ||
    checkArraysAreSame(
      stripEmpty(dataHeaders),
      stripEmpty(buildTemplateHeaders(true))
    );

  if (!headersMatch) {
    return [
      `Column names don't match. Please ensure they follow the latest template.`
    ];
  }
  return [];
};

const validateName = (name: string | null, index: number): string[] =>
  !name || name.length < 3 ? [`Row ${index + 1}: Invalid name`] : [];

const validatePhoneNumbers = (
  member: AddMemberInput,
  index: number,
  countryCodeList: string[],
  countryCodeMapping: Record<string, string>,
  primaryNumbers: string[],
  altNumbers: string[]
) => {
  const { name, primaryNumber, altNumber, primaryNumberCode, altNumberCode } =
    member;
  const errors: string[] = [];

  if (
    !primaryNumberCode ||
    (primaryNumberCode && !countryCodeList.includes(`${primaryNumberCode}`))
  ) {
    errors.push(`Row ${index + 1}: Invalid Primary Number Country Code`);
  }

  const primaryNumberWithCode = `+${primaryNumberCode}${primaryNumber}`;

  if (
    !primaryNumber ||
    (primaryNumber &&
      primaryNumberCode &&
      !isValidPhoneNumber(primaryNumber, countryCodeMapping[primaryNumberCode]))
  )
    errors.push(`Row ${index + 1}: Invalid Primary number format`);

  if (primaryNumbers.includes(`${name}_${primaryNumberWithCode}`))
    errors.push(`Row ${index + 1}: Duplicate Name and Primary Number`);

  if (altNumberCode && !countryCodeList.includes(`${altNumberCode}`)) {
    errors.push(`Row ${index + 1}: Invalid Alternative Number Country Code`);
  }

  const altNumberWithCode = `+${altNumberCode}${altNumber}`;
  if (
    altNumber &&
    (!altNumberCode ||
      !isValidPhoneNumber(altNumber, countryCodeMapping[altNumberCode]))
  )
    errors.push(`Row ${index + 1}: Invalid Alternative number format`);

  if (altNumbers.includes(`${name}_${altNumberWithCode}`))
    errors.push(`Row ${index + 1}: Duplicate Name Alternative Number`);

  if (altNumber && primaryNumberWithCode === altNumberWithCode)
    errors.push(
      `Row ${index + 1}: Primary and Alternative Number should not be same`
    );

  return { errors, primaryNumberWithCode, altNumberWithCode };
};

const validateEmail = (email: string | null, index: number): string[] =>
  email && isInvalidEmail(email) ? [`Row ${index + 1}: Invalid Email`] : [];

const validateAttendanceBasedFee = (
  member: AddMemberInput,
  index: number,
  minAmount: number,
  maxAmount: number,
  isSessionBased?: boolean
): string[] => {
  const errors: string[] = [];
  const { ratePerClass } = member as any;

  if (ratePerClass) {
    if (Number(ratePerClass).toLocaleString() === 'NaN')
      errors.push(`Row ${index + 1}: Invalid rate per class`);
    else if (
      Number(ratePerClass) !== 0 &&
      (Number(ratePerClass) < minAmount || Number(ratePerClass) > maxAmount)
    )
      errors.push(
        `Row ${
          index + 1
        }: Rate per class should be between ${minAmount} and ${maxAmount}`
      );
    else if (Number(ratePerClass) !== 0 && Number(ratePerClass) < 2)
      errors.push(`Row ${index + 1}: Rate per class must be at least 2`);
    else if (
      hasDecimalPoint(ratePerClass) &&
      String(ratePerClass).split('.')[1]?.length > 2
    )
      errors.push(
        `Row ${index + 1}: Rate per class allows up to 2 decimal places`
      );
  }

  if (isSessionBased) {
    const billingValue = (member as any)['billingPeriod(numberOfClasses)'];

    if (billingValue) {
      if (
        Number(billingValue).toLocaleString() === 'NaN' ||
        !Number.isInteger(Number(billingValue))
      )
        errors.push(`Row ${index + 1}: Invalid number of classes`);
      else if (Number(billingValue) < 1 || Number(billingValue) > 9999)
        errors.push(
          `Row ${index + 1}: Number of classes should be between 1 and 9999`
        );
    }
  } else {
    const memberStartDate = (member as any).startDate;
    if (memberStartDate && !parseDateAuto(memberStartDate))
      errors.push(`Row ${index + 1}: Invalid start date`);
  }

  return errors;
};

const validateEndDate = (
  member: AddMemberInput,
  index: number,
  groupEndDate?: string,
  groupStartDate?: string
): string[] => {
  const { startDate, endDate } = member;
  const parsedEndDate = parseDateAuto(endDate);
  const parsedStartDate = startDate ? parseDateAuto(startDate) : null;
  const parsedGroupStartDate = groupStartDate
    ? parseDateAuto(groupStartDate)
    : null;
  const parsedGroupEndDate = groupEndDate ? parseDateAuto(groupEndDate) : null;

  if (parsedEndDate && parsedStartDate && parsedEndDate < parsedStartDate) {
    return [`Row ${index + 1}: End date cannot be before start date`];
  }
  if (
    parsedEndDate &&
    parsedEndDate < new Date(new Date().setHours(0, 0, 0, 0))
  ) {
    return [`Row ${index + 1}: End date cannot be a past date`];
  }
  if (
    parsedGroupEndDate &&
    parsedEndDate &&
    parsedGroupEndDate < parsedEndDate
  ) {
    return [`Row ${index + 1}: End date cannot be after group end date`];
  }
  if (
    !parsedStartDate &&
    parsedEndDate &&
    parsedGroupStartDate &&
    parsedEndDate < parsedGroupStartDate
  ) {
    return [`Row ${index + 1}: End date can't be before group start date.`];
  }
  return [];
};

const validateLeadSquaredFee = (
  courseFee: AddMemberInput['courseFee'],
  index: number,
  minAmount: number,
  maxAmount: number
): string[] => {
  const errors: string[] = [];
  if (courseFee && Number(courseFee) !== 0 && Number(courseFee) < minAmount) {
    errors.push(`Row ${index + 1}: Amount cannot be less than ${minAmount}`);
  }
  if (courseFee && Number(courseFee) > maxAmount) {
    errors.push(`Row ${index + 1}: Amount cannot be greater than ${maxAmount}`);
  }
  return errors;
};

const validateFeeAmount = ({
  member,
  index,
  isCustomTerm,
  feeCategories,
  termsName,
  minAmount,
  maxAmount,
  termsData
}: {
  member: AddMemberInput;
  index: number;
  isCustomTerm: boolean;
  feeCategories: string[];
  termsName: string[];
  minAmount: number;
  maxAmount: number;
  termsData?: TermData[];
}): string[] => {
  if (isCustomTerm) {
    return customTermFileValidation(
      feeCategories,
      termsName,
      member,
      index,
      minAmount,
      maxAmount,
      termsData
    );
  }

  if (feeCategories.length > 0) {
    const errors: string[] = [];
    let totalAmount = 0;
    let hasEmptyCategoryValue = false;
    let hasNegativeValue = false;
    feeCategories.forEach((category) => {
      const categoryValue = member[category as keyof AddMemberInput];
      const isCategoryValueEmpty =
        categoryValue === undefined || categoryValue === null;
      if (!hasEmptyCategoryValue && isCategoryValueEmpty)
        hasEmptyCategoryValue = true;
      if (
        typeof categoryValue === 'number' &&
        categoryValue < 0 &&
        !hasNegativeValue
      )
        hasNegativeValue = true;
      if (categoryValue && Number(categoryValue).toLocaleString() === 'NaN') {
        errors.push(`Row ${index + 1}: Invalid amount for ${category}`);
      } else {
        totalAmount += Number(categoryValue);
      }
    });
    totalAmount = roundTo2(totalAmount);
    if (
      (!hasEmptyCategoryValue &&
        totalAmount !== 0 &&
        totalAmount < minAmount) ||
      totalAmount > maxAmount ||
      hasNegativeValue
    )
      errors.push(
        `Row ${
          index + 1
        }: Amount should be between ${minAmount} and ${maxAmount}`
      );
    return errors;
  }

  const errors: string[] = [];
  const { amount } = member;
  if (amount && Number(amount).toLocaleString() === 'NaN')
    errors.push(`Row ${index + 1}: Invalid amount`);

  if (
    amount &&
    Number(amount) !== 0 &&
    (Number(amount) < minAmount || Number(amount) > maxAmount)
  )
    errors.push(
      `Row ${index + 1}: Amount should be between ${minAmount} and ${maxAmount}`
    );
  if (amount && hasMoreThanTwoDecimalPlaces(amount))
    errors.push(
      `Row ${index + 1}: Amount cannot have more than 2 decimal places`
    );
  return errors;
};

const validateAmountPaid = (
  member: AddMemberInput,
  index: number,
  minAmount: number,
  maxAmount: number
): string[] => {
  const { amountPaid, amount } = member;
  if (amountPaid && Number(amountPaid).toLocaleString() === 'NaN')
    return [`Row ${index + 1}: Invalid amount paid`];
  if (
    amountPaid &&
    (Number(amountPaid) < minAmount || Number(amountPaid) > maxAmount)
  )
    return [
      `Row ${
        index + 1
      }: Amount paid should be between ${minAmount} and ${maxAmount}`
    ];
  if (
    amountPaid &&
    amount &&
    Number(amount).toLocaleString() !== 'NaN' &&
    Number(amountPaid) > Number(amount)
  )
    return [
      `Row ${index + 1}: Amount paid should be less than or equal to Amount`
    ];
  return [];
};

const validateFrequency = (
  frequency: string | null | undefined,
  index: number
): string[] => {
  if (!frequency) return [];
  const filteredOptions = PaymentCollectionOptions.filter(
    (option: { label: string }) => option.label !== 'Once'
  );
  const options = filteredOptions.map(
    (option: { label: string }) => option.label
  );
  if (!options.includes(frequency))
    return [
      `Row ${index + 1}: Frequency should be one of the provided list values`
    ];
  return [];
};

const validateMonthsPerTerm = (
  member: AddMemberInput,
  index: number
): string[] => {
  const { monthsPerTerm, frequency } = member;
  if (monthsPerTerm) {
    if (frequency !== 'By Term/Installments') {
      return [
        `Row ${
          index + 1
        }: Months per term should be left empty for frequencies other than ‘By Term/Installments‘`
      ];
    }
    if (Number(monthsPerTerm) < 1 || Number(monthsPerTerm) > 12)
      return [
        `Row ${
          index + 1
        }: Months per term should be within the range of 1 to 12`
      ];
  } else if (frequency === 'By Term/Installments')
    return [
      `Row ${
        index + 1
      }: Months per term cannot be empty for frequency ‘By Term/Installments’`
    ];
  return [];
};

const validateStartDateAndFrequency = (
  member: AddMemberInput,
  index: number,
  isAttendanceBasedFee?: boolean,
  isSessionBased?: boolean
): string[] => {
  const { startDate, frequency } = member;
  const errors: string[] = [];

  if (startDate) {
    const { date, error } = parseDateString(startDate);
    if (error) errors.push(`Row ${index + 1}: ${error}`);
    if (date && date.getTime() < new Date().setHours(0, 0, 0, 0))
      errors.push(
        `Row ${index + 1}: Start date should be greater than or equal to today`
      );
  } else if (frequency)
    errors.push(
      `Row ${index + 1}: Start date is required when frequency is specified`
    );

  if (startDate && !frequency && !(isAttendanceBasedFee && !isSessionBased))
    errors.push(
      `Row ${index + 1}: Frequency is required when start date is specified`
    );

  return errors;
};

const validateGuardianName = (
  guardianName: string | null,
  index: number
): string[] =>
  guardianName && guardianName.length < 3
    ? [`Row ${index + 1}: Invalid Guardian Name`]
    : [];

const validateAddress = (
  member: AddMemberInput,
  index: number,
  countryList: string[],
  stateList: string[]
): string[] => {
  const { country, state, city, pincode } = member;
  const errors: string[] = [];

  if (country) {
    if (!countryList.includes(country))
      errors.push(
        `Row ${index + 1}: Country should be one of the provided list values`
      );
    if (country === 'Others' && (state || city || pincode))
      errors.push(
        `Row ${
          index + 1
        }: For Non-Indian countries, State, City, and Pincode fields should remain empty`
      );
  }

  if (state) {
    if (!stateList.includes(state))
      errors.push(
        `Row ${index + 1}: State should be one of the provided list values`
      );
    if (!country)
      errors.push(
        `Row ${index + 1}: Please select a Country before entering State`
      );
  }

  if (city && !state)
    errors.push(`Row ${index + 1}: Please select a State before entering City`);

  if (
    pincode &&
    !(/^\d+$/.test(pincode.toString()) && pincode.toString().length === 6)
  ) {
    errors.push(`Row ${index + 1}: Invalid Pincode`);
  }

  return errors;
};

const validateBloodGroupAndInsurance = (
  member: AddMemberInput,
  index: number
): string[] => {
  const errors: string[] = [];
  if (member.bloodGroup && !isValidBloodGroup(member.bloodGroup))
    errors.push(`Row ${index + 1}: Invalid blood group format`);
  if (member.isInsured && !isValidInsureValue(member.isInsured))
    errors.push(`Row ${index + 1}: Invalid is insured value`);
  return errors;
};

const validateDateOfBirth = (
  dateOfBirth: string | null,
  index: number
): string[] => {
  if (!dateOfBirth) return [];
  const errors: string[] = [];
  const { date, error } = parseDateString(dateOfBirth);
  if (error) errors.push(`Row ${index + 1}: ${error}`);
  if (date && date.getTime() > new Date().setHours(0, 0, 0, 0))
    errors.push(`Row ${index + 1}: Date of birth should not be a future date`);
  return errors;
};

const validateAdmissionDate = (
  admissionDate: string | null,
  index: number
): string[] => {
  if (!admissionDate) return [];
  const { error } = parseDateString(admissionDate);
  return error ? [`Row ${index + 1}: ${error}`] : [];
};

const validateAdmissionNumberDuplicate = (
  admissionNumber: string | null,
  index: number,
  admissionNumbers: string[]
): string[] =>
  admissionNumber && admissionNumbers.includes(admissionNumber)
    ? [`Row ${index + 1}: Duplicate Admission Number`]
    : [];

const validateCustomFields = (
  member: AddMemberInput,
  index: number,
  customFields: UserProfileCustomField[]
): string[] => {
  const errors: string[] = [];
  const memberAsAny = member as Record<string, any>;

  customFields?.forEach((field) => {
    const rawValue = memberAsAny[field.label];
    if (!rawValue && rawValue !== 0) return;
    const strValue = `${rawValue}`.trim();
    if (!strValue) return;

    switch (field.type) {
      case 'bool':
        if (!isValidInsureValue(strValue)) {
          errors.push(`Row ${index + 1}: ${field.label} must be Yes or No`);
        }
        break;
      case 'number':
        if (Number.isNaN(Number(strValue))) {
          errors.push(
            `Row ${index + 1}: ${field.label} must be a valid number`
          );
        }
        break;
      case 'date': {
        const { error: dateErr } = parseDateString(strValue);
        if (dateErr) {
          errors.push(`Row ${index + 1}: ${field.label}: ${dateErr}`);
        }
        break;
      }
      case 'varchar':
        if (strValue.length > 100) {
          errors.push(
            `Row ${index + 1}: ${field.label} must be 100 characters or fewer`
          );
        }
        break;
      case 'textarea':
        if (strValue.length > 500) {
          errors.push(
            `Row ${index + 1}: ${field.label} must be 500 characters or fewer`
          );
        }
        break;
      default:
        break;
    }
  });

  return errors;
};

export const getValidationErrorsOnMemberDetails = (
  dataHeaders: string[],
  memberDetails: AddMemberInput[],
  maxAmount: number,
  minAmount: number,
  feeCategories: string[],
  isEmailEnabled: boolean,
  isAmountPaidTillDateEnabled: boolean,
  isOnce: boolean,
  customFields: UserProfileCustomField[],
  countryCodeList: string[],
  countryCodeMapping: Record<string, string>,
  termsName: string[],
  isCustomTerm: boolean,
  isLeadSquared: boolean,
  isEndDateEnabled?: boolean,
  groupEndDate?: string,
  groupStartDate?: string,
  isAttendanceBasedFee?: boolean,
  isSessionBased?: boolean,
  termsData?: TermData[]
): string[] => {
  const headerErrors = validateHeaders(
    dataHeaders,
    feeCategories,
    isEmailEnabled,
    isAmountPaidTillDateEnabled,
    isOnce,
    customFields,
    termsName,
    isLeadSquared,
    isCustomTerm,
    isEndDateEnabled,
    isAttendanceBasedFee,
    isSessionBased
  );
  if (headerErrors.length > 0) return headerErrors;

  const countryList = countries.map((country) => country).concat('Others');
  const stateList = Object.keys(cities).map((state) => state);

  const errors: string[] = [];
  const primaryNumbers: string[] = [];
  const altNumbers: string[] = [];
  const admissionNumbers: string[] = [];

  memberDetails.forEach((member: AddMemberInput, index: number) => {
    const { name, primaryNumber, altNumber, admissionNumber } = member;

    errors.push(...validateName(name, index));

    const {
      errors: phoneNumberErrors,
      primaryNumberWithCode,
      altNumberWithCode
    } = validatePhoneNumbers(
      member,
      index,
      countryCodeList,
      countryCodeMapping,
      primaryNumbers,
      altNumbers
    );
    errors.push(...phoneNumberErrors);

    errors.push(...validateEmail(member.email, index));

    if (isAttendanceBasedFee) {
      errors.push(
        ...validateAttendanceBasedFee(
          member,
          index,
          minAmount,
          maxAmount,
          isSessionBased
        )
      );
    }

    if (isEndDateEnabled) {
      errors.push(
        ...validateEndDate(member, index, groupEndDate, groupStartDate)
      );
    }

    if (isLeadSquared) {
      errors.push(
        ...validateLeadSquaredFee(member.courseFee, index, minAmount, maxAmount)
      );
    }

    errors.push(
      ...validateFeeAmount({
        member,
        index,
        isCustomTerm,
        feeCategories,
        termsName,
        minAmount,
        maxAmount,
        termsData
      })
    );

    errors.push(...validateAmountPaid(member, index, minAmount, maxAmount));
    errors.push(...validateFrequency(member.frequency, index));
    errors.push(...validateMonthsPerTerm(member, index));
    errors.push(
      ...validateStartDateAndFrequency(
        member,
        index,
        isAttendanceBasedFee,
        isSessionBased
      )
    );
    errors.push(...validateGuardianName(member.guardianName, index));
    errors.push(...validateAddress(member, index, countryList, stateList));
    errors.push(...validateBloodGroupAndInsurance(member, index));
    errors.push(...validateDateOfBirth(member.dateOfBirth, index));
    errors.push(...validateAdmissionDate(member.admissionDate, index));
    errors.push(
      ...validateAdmissionNumberDuplicate(
        admissionNumber,
        index,
        admissionNumbers
      )
    );
    errors.push(...validateCustomFields(member, index, customFields));

    if (primaryNumber) primaryNumbers.push(`${name}_${primaryNumberWithCode}`);
    if (altNumber) altNumbers.push(`${name}_${altNumberWithCode}`);
    if (admissionNumber) admissionNumbers.push(admissionNumber);
  });

  return errors;
};
